import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown } from "lucide-react";

import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui";

import { Procedure, ProcedureResponse } from "@/types";

import { FormProcedure } from "../../forms";
import { Pagination } from "../../pagination";

type ProcedureDataTableProps = {
  data: ProcedureResponse;
};

type ColunsProcedureProps = {
  id: string;
  tenant_id: string;
  name: string;
  value: number;
  time: number;
  exam: string;
  covenant: string;
  created_at: string;
  updated_at: string;
  procedure: Procedure;
};

export function ProcedureDataTable({
  data: { items, data },
}: ProcedureDataTableProps) {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  function getData() {
    const data = items.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.value,
      time: item.time,
      exam: item.exam === "sim" ? t("LABEL.yes") : t("LABEL.no"),
      agreement: item.covenant === "sim" ? t("LABEL.yes") : t("LABEL.no"),
      procedure: item,
    }));

    return data;
  }

  const columns: ColumnDef<ColunsProcedureProps>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.name")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="normal-case">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "value",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.value")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">R$ {row.getValue("value")}</div>
      ),
    },
    // {
    //   accessorKey: "time",
    //   header: ({ column }) => {
    //     return (
    //       <Button
    //         variant="ghost"
    //         className="px-0"
    //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       >
    //         {t("LABEL.time")}
    //         <ArrowUpDown className="ml-2 h-4 w-4" />
    //       </Button>
    //     );
    //   },
    //   cell: ({ row }) => (
    //     <div className="capitalize">{row.getValue("time")}</div>
    //   ),
    // },
    // {
    //   accessorKey: "exam",
    //   header: ({ column }) => {
    //     return (
    //       <Button
    //         variant="ghost"
    //         className="px-0"
    //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       >
    //         {t("LABEL.exam")}
    //         <ArrowUpDown className="ml-2 h-4 w-4" />
    //       </Button>
    //     );
    //   },
    //   cell: ({ row }) => (
    //     <div className="capitalize">{row.getValue("exam")}</div>
    //   ),
    // },
    // {
    //   accessorKey: "agreement",
    //   header: ({ column }) => {
    //     return (
    //       <Button
    //         variant="ghost"
    //         className="px-0"
    //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       >
    //         {t("LABEL.agreement")}
    //         <ArrowUpDown className="ml-2 h-4 w-4" />
    //       </Button>
    //     );
    //   },
    //   cell: ({ row }) => (
    //     <div className="capitalize">{row.getValue("agreement")}</div>
    //   ),
    // },
  ];


  const dataMemo = useMemo(() => getData(), [items]);

  const columnsMemo: any = useMemo(() => columns, []);

  const table = useReactTable({
    data: dataMemo,
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center py-4 justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="ml-auto bg-zinc-50 outline-none"
            >
              {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    // onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onCheckedChange={(value) => {
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [column.id]: !!value,
                      }));
                    }}
                  >
                    {t(`LABEL.${column.id}`)}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <>
                        <TableCell key={cell.id}>
                          <FormProcedure procedure={row.original.procedure}>
                            <div className="cursor-pointer">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </FormProcedure>
                        </TableCell>
                      </>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("No results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={data} />
    </div>
  );
}
